import { useState, useEffect, useCallback, useRef } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import type { DateValidationError } from '@mui/x-date-pickers/models/validation';
import type { EditableFieldSharedProps } from './types';
import { updateVariableValue } from 'components/editor/grid/reduxStore/allVariableSlice';

export const toValidDayjs = (value: unknown): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value as string);
  return parsed.isValid() ? parsed : null;
};

type Props = Pick<EditableFieldSharedProps, 'field' | 'mutate' | 'dispatch' | 'currentValue'>;

export function useCustomFieldDateInput({ field, mutate, dispatch, currentValue }: Props) {
  const [localValue, setLocalValue] = useState<Dayjs | null>(() => toValidDayjs(currentValue));
  const [hasError, setHasError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const iconButtonRef = useRef<HTMLButtonElement>(null);
  const currentValueRef = useRef(currentValue);

  useEffect(() => {
    currentValueRef.current = currentValue;
    setLocalValue(toValidDayjs(currentValue));
  }, [currentValue]);

  const persist = useCallback(
    (date: Dayjs | null) => {
      const isoDate = date?.isValid() ? date.format('YYYY-MM-DD') : null;
      dispatch(updateVariableValue({ variableName: field.name, newValue: isoDate }));
      mutate({ id: field.id, type: field.type, value: isoDate });
    },
    [dispatch, field, mutate]
  );

  const handleChange = useCallback((newValue: Dayjs | null) => {
    setLocalValue(newValue);
  }, []);

  const handleCalendarSelect = useCallback(
    (selectedDate: Dayjs) => {
      setLocalValue(selectedDate);
      persist(selectedDate);
      setIsOpen(false);
    },
    [persist]
  );

  const handleError = useCallback((error: DateValidationError | null) => {
    setHasError(error !== null);
  }, []);

  const handleBlur = useCallback(() => {
    if (localValue?.isValid()) {
      persist(localValue);
      return;
    }
    if (localValue === null) {
      if (!hasError) {
        persist(null);
      } else {
        setLocalValue(toValidDayjs(currentValueRef.current));
      }
    }
  }, [localValue, hasError, persist]);

  return { localValue, isOpen, setIsOpen, iconButtonRef, handleChange, handleCalendarSelect, handleError, handleBlur };
}
